import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { fetchEstimates, updateEstimateStatus, deleteEstimate, Estimate, getEstimatePreviewHtml } from '@/services/estimateService';
import {
  buildDevisHtml,
  deleteDevisFromHistory,
  downloadDevisPdf,
  formatMoneyTn,
  formatSerialDisplay,
  listDevisHistory,
  SavedDevisRecord,
} from '@/services/devisService';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Check, X, AlertCircle, Trash2, Eye, CheckCircle, Send, Printer, Mail, Plus, Download, FileText, Inbox } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useLanguage } from '@/context/LanguageContext';
import DevisGenerator from '@/pages/admin/DevisGenerator';
import { cn } from '@/lib/utils';

type DevisViewMode = 'made' | 'requested';

const EstimatesManagement: React.FC = () => {
  const [viewMode, setViewMode] = useState<DevisViewMode>('made');
  const [madeDevis, setMadeDevis] = useState<SavedDevisRecord[]>([]);
  const [viewingEstimate, setViewingEstimate] = useState<Estimate | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewTitle, setPreviewTitle] = useState('Aperçu devis');
  const [isDevisOpen, setIsDevisOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { language } = useLanguage();

  const logoUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/MKLogo-dark.png` : '/MKLogo-dark.png';

  const refreshMadeDevis = useCallback(() => {
    setMadeDevis(listDevisHistory());
  }, []);

  useEffect(() => {
    refreshMadeDevis();
  }, [refreshMadeDevis]);

  const { data: estimates = [], isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'estimates'],
    queryFn: fetchEstimates,
  });

  useEffect(() => {
    if (error) {
      console.error('Error fetching estimates:', error);
    }
    if (estimates && estimates.length > 0) {
      console.log('Estimates loaded successfully:', estimates);
    }
  }, [error, estimates]);

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Estimate['status'] }) => {
      try {
        console.log(`Starting mutation to update estimate ${id} to ${status}`);
        // First update the estimate status in the database
        const updatedEstimate = await updateEstimateStatus(id, status);
        
        // Then try to send a notification, but don't fail the whole operation if this part fails
        try {
          console.log('Sending notification for status update:', { id, status });
          const { data, error } = await supabase.functions.invoke('notify-estimate-status', {
            body: { id, status },
          });
          
          if (error) {
            console.error('Error sending notification:', error);
            // Just log the error but don't throw
            toast({
              title: 'Status updated',
              description: 'Estimate status updated, but notification could not be sent.',
              variant: 'default',
            });
          } else {
            console.log('Notification sent successfully:', data);
            toast({
              title: 'Status updated',
              description: 'Estimate status updated and notification sent.',
              variant: 'default',
            });
          }
        } catch (error) {
          console.error('Failed to send notification:', error);
          // Just log the error but don't throw
          toast({
            title: 'Status updated',
            description: 'Estimate status updated, but notification could not be sent.',
            variant: 'default',
          });
        }
        
        // Return the updated estimate regardless of notification status
        return updatedEstimate;
      } catch (error) {
        console.error('Error updating estimate status:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'estimates'] });
      setIsViewDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error updating estimate status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update the estimate status.',
        variant: 'destructive',
      });
    },
  });

  const deleteEstimateMutation = useMutation({
    mutationFn: deleteEstimate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'estimates'] });
      toast({
        title: 'Estimate deleted',
        description: 'The estimate has been successfully deleted.',
      });
    },
    onError: (error) => {
      console.error('Error deleting estimate:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete the estimate.',
        variant: 'destructive',
      });
    },
  });

  const formatShippingAddress = (address: string | { street: string; city: string; state: string; zip: string; country: string; }): string => {
    if (typeof address === 'string') {
      return address;
    } else {
      return `${address.street || ''}\n${address.city || ''}, ${address.state || ''} ${address.zip || ''}\n${address.country || ''}`.trim();
    }
  };

  const getStatusBadge = (status: Estimate['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Rejected</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Completed</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { 
        addSuffix: true,
        locale: language === 'fr' ? fr : enUS
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid date';
    }
  };

  const handleViewEstimate = (estimate: Estimate) => {
    setViewingEstimate(estimate);
    setIsViewDialogOpen(true);
  };

  const handleUpdateStatus = (id: string, status: Estimate['status']) => {
    console.log(`Handling status update request for estimate ${id} to ${status}`);
    updateStatusMutation.mutate({ id, status });
  };

  const handleDeleteEstimate = (id: string) => {
    deleteEstimateMutation.mutate(id);
  };

  const handlePreviewEstimate = async (estimate: Estimate) => {
    try {
      const productIds = estimate.items.map(item => item.product_id);
      let items = estimate.items;
      
      if (productIds.length > 0) {
        const { data: products } = await supabase
          .from('products')
          .select('id, name, price, image')
          .in('id', productIds);
        
        if (products && products.length > 0) {
          items = estimate.items.map(item => {
            const product = products.find(p => p.id === item.product_id);
            return {
              ...item,
              product: product || { name: item.name }
            };
          });
        }
      }
      
      const html = getEstimatePreviewHtml(estimate, items);
      setPreviewHtml(html);
      setPreviewTitle('Aperçu demande client');
      setIsPreviewDialogOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate estimate preview.',
        variant: 'destructive',
      });
    }
  };

  const handlePreviewMadeDevis = (record: SavedDevisRecord) => {
    setPreviewHtml(buildDevisHtml(record.draft, logoUrl));
    setPreviewTitle(formatSerialDisplay(record.draft.serialNumber));
    setViewingEstimate(null);
    setIsPreviewDialogOpen(true);
  };

  const handleDownloadMadeDevis = async (record: SavedDevisRecord) => {
    setDownloadingId(record.id);
    try {
      await downloadDevisPdf(record.draft, logoUrl);
      toast({
        title: 'PDF téléchargé',
        description: formatSerialDisplay(record.draft.serialNumber),
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Erreur PDF',
        description: 'Impossible de régénérer le PDF.',
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteMadeDevis = (id: string) => {
    deleteDevisFromHistory(id);
    refreshMadeDevis();
    toast({
      title: 'Devis supprimé',
      description: 'Le devis a été retiré de l’historique.',
    });
  };

  const handleSendPreview = async (estimate: Estimate) => {
    try {
      const productIds = estimate.items.map(item => item.product_id);
      
      let items = estimate.items;
      
      if (productIds.length > 0) {
        const { data: products } = await supabase
          .from('products')
          .select('id, name, price, image')
          .in('id', productIds);
        
        if (products && products.length > 0) {
          items = estimate.items.map(item => {
            const product = products.find(p => p.id === item.product_id);
            return {
              ...item,
              product: product || { name: item.name }
            };
          });
        }
      }
      
      const { error } = await supabase.functions.invoke('send-estimate', {
        body: { 
          email: estimate.contact_email,
          estimate,
          items,
          language: 'en'
        }
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Preview sent',
        description: `Estimate preview sent to ${estimate.contact_email}`,
      });
    } catch (error) {
      console.error('Error sending preview:', error);
      toast({
        title: 'Error',
        description: 'Failed to send estimate preview.',
        variant: 'destructive',
      });
    }
  };

  const estimatesArray = Array.isArray(estimates) ? estimates : [];
  
  const pendingEstimates = estimatesArray.filter(e => e.status === 'pending');
  const approvedEstimates = estimatesArray.filter(e => e.status === 'approved');
  const rejectedEstimates = estimatesArray.filter(e => e.status === 'rejected');
  const completedEstimates = estimatesArray.filter(e => e.status === 'completed');

  const renderEstimateActions = (estimate: Estimate) => (
    <div className="flex flex-wrap items-center gap-1">
      <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => handleViewEstimate(estimate)}>
        <Eye size={16} />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 text-purple-600 hover:bg-purple-50 hover:text-purple-700"
        onClick={() => handlePreviewEstimate(estimate)}
      >
        <Printer size={16} />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
        onClick={() => handleSendPreview(estimate)}
      >
        <Mail size={16} />
      </Button>
      {estimate.status === 'pending' && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-green-600 hover:bg-green-50 hover:text-green-700"
            onClick={() => handleUpdateStatus(estimate.id, 'approved')}
          >
            <Check size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => handleUpdateStatus(estimate.id, 'rejected')}
          >
            <X size={16} />
          </Button>
        </>
      )}
      {estimate.status === 'approved' && (
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
          onClick={() => handleUpdateStatus(estimate.id, 'completed')}
        >
          <CheckCircle size={16} />
        </Button>
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="icon" className="h-10 w-10 text-red-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 size={16} />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette demande ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette demande client sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={() => handleDeleteEstimate(estimate.id)}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  const renderEstimateTable = (filteredEstimates: Estimate[]) => (
    <>
      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {filteredEstimates.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Aucune demande trouvée</p>
        ) : (
          filteredEstimates.map((estimate) => (
            <div key={estimate.id} className="rounded-2xl border border-border/70 bg-white p-4 shadow-soft">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{estimate.contact_email}</p>
                  <p className="text-sm text-muted-foreground">{estimate.contact_phone}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {estimate.items?.length || 0} articles · {formatDate(estimate.created_at)}
                  </p>
                </div>
                {getStatusBadge(estimate.status)}
              </div>
              {renderEstimateActions(estimate)}
            </div>
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEstimates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">
                  Aucune demande trouvée
                </TableCell>
              </TableRow>
            ) : (
              filteredEstimates.map((estimate) => (
                <TableRow key={estimate.id}>
                  <TableCell className="font-mono text-xs">{estimate.id.slice(0, 8)}...</TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{estimate.contact_email}</div>
                      <div className="text-sm text-muted-foreground">{estimate.contact_phone}</div>
                    </div>
                  </TableCell>
                  <TableCell>{estimate.items?.length || 0} articles</TableCell>
                  <TableCell className="text-sm">{formatDate(estimate.created_at)}</TableCell>
                  <TableCell>{getStatusBadge(estimate.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">{renderEstimateActions(estimate)}</div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );

  const renderMadeDevisTable = () => (
    <>
      <div className="space-y-3 md:hidden">
        {madeDevis.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Aucun devis créé pour le moment. Appuyez sur « Générer un devis ».
          </p>
        ) : (
          madeDevis.map((record) => (
            <div key={record.id} className="rounded-2xl border border-border/70 bg-white p-4 shadow-soft">
              <div className="mb-1 flex items-start justify-between gap-3">
                <p className="font-semibold">{formatSerialDisplay(record.draft.serialNumber)}</p>
                <p className="shrink-0 text-sm font-medium">
                  {formatMoneyTn(record.totals.totalAfterDiscount)}
                </p>
              </div>
              <p className="font-medium">{record.draft.clientName || '—'}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {record.draft.items?.length || 0} articles · {formatDate(record.createdAt)}
              </p>
              <div className="mt-3 flex flex-wrap gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-purple-600 hover:bg-purple-50"
                  onClick={() => handlePreviewMadeDevis(record)}
                >
                  <Eye size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-blue-600 hover:bg-blue-50"
                  disabled={downloadingId === record.id}
                  onClick={() => handleDownloadMadeDevis(record)}
                >
                  <Download size={16} />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-red-600 hover:bg-red-50">
                      <Trash2 size={16} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer ce devis ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {formatSerialDisplay(record.draft.serialNumber)} sera retiré de l’historique local.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-500 hover:bg-red-600"
                        onClick={() => handleDeleteMadeDevis(record.id)}
                      >
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>N° devis</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Articles</TableHead>
              <TableHead>Total HT</TableHead>
              <TableHead>Créé</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {madeDevis.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  Aucun devis créé pour le moment. Cliquez sur « Générer un devis ».
                </TableCell>
              </TableRow>
            ) : (
              madeDevis.map((record) => (
                <TableRow key={record.id}>
                  <TableCell className="whitespace-nowrap font-medium">
                    {formatSerialDisplay(record.draft.serialNumber)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{record.draft.clientName || '—'}</div>
                      {record.draft.clientAddress ? (
                        <div className="line-clamp-1 text-sm text-muted-foreground">
                          {record.draft.clientAddress}
                        </div>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>{record.draft.items?.length || 0}</TableCell>
                  <TableCell className="whitespace-nowrap font-medium">
                    {formatMoneyTn(record.totals.totalAfterDiscount)}
                  </TableCell>
                  <TableCell className="text-sm">{formatDate(record.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-purple-600 hover:bg-purple-50 hover:text-purple-700"
                        onClick={() => handlePreviewMadeDevis(record)}
                      >
                        <Eye size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                        disabled={downloadingId === record.id}
                        onClick={() => handleDownloadMadeDevis(record)}
                      >
                        <Download size={16} />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer ce devis ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {formatSerialDisplay(record.draft.serialNumber)} sera retiré de l’historique local.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-red-500 hover:bg-red-600"
                              onClick={() => handleDeleteMadeDevis(record.id)}
                            >
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );

  return (
    <div>
      <Helmet>
        <title>Devis | Admin Dashboard</title>
      </Helmet>

      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-sans text-xl font-semibold sm:text-2xl">Devis</h1>
          <p className="text-sm text-muted-foreground">
            Devis créés et demandes clients.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <Button
            variant="outline"
            className="h-11"
            onClick={() => {
              refreshMadeDevis();
              refetch();
            }}
          >
            Refresh
          </Button>
          <Button
            onClick={() => setIsDevisOpen(true)}
            className="h-11 bg-furniture-gold text-black hover:bg-furniture-gold-hover"
          >
            <Plus className="mr-1.5 h-4 w-4 sm:mr-2" />
            <span className="sm:hidden">Nouveau</span>
            <span className="hidden sm:inline">Générer un devis</span>
          </Button>
        </div>
      </div>

      <div className="mb-4 flex w-full rounded-full border border-border bg-muted/40 p-1">
        <button
          type="button"
          onClick={() => setViewMode('made')}
          className={cn(
            'inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-2 text-xs font-medium transition-colors sm:gap-2 sm:px-4 sm:text-sm',
            viewMode === 'made'
              ? 'bg-furniture-gold text-black shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <FileText className="h-4 w-4 shrink-0" />
          <span className="truncate">Créés</span>
          <span
            className={cn(
              'rounded-full px-1.5 py-0.5 text-[10px] tabular-nums sm:px-2 sm:text-xs',
              viewMode === 'made' ? 'bg-black/10' : 'bg-muted'
            )}
          >
            {madeDevis.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setViewMode('requested')}
          className={cn(
            'inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-2 text-xs font-medium transition-colors sm:gap-2 sm:px-4 sm:text-sm',
            viewMode === 'requested'
              ? 'bg-furniture-gold text-black shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Inbox className="h-4 w-4 shrink-0" />
          <span className="truncate">Demandes</span>
          <span
            className={cn(
              'rounded-full px-1.5 py-0.5 text-[10px] tabular-nums sm:px-2 sm:text-xs',
              viewMode === 'requested' ? 'bg-black/10' : 'bg-muted'
            )}
          >
            {estimatesArray.length}
          </span>
          {pendingEstimates.length > 0 && (
            <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              {pendingEstimates.length}
            </span>
          )}
        </button>
      </div>

      {viewMode === 'made' ? (
        <Card>
          <CardHeader>
            <CardTitle>Devis créés</CardTitle>
          </CardHeader>
          <CardContent>{renderMadeDevisTable()}</CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Demandes clients</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" role="status">
                  <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
                </div>
                <p className="mt-4 text-muted-foreground">Chargement des demandes…</p>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-500">
                <AlertCircle className="mx-auto h-8 w-8" />
                <p className="mt-4">Erreur de chargement. Réessayez.</p>
              </div>
            ) : estimatesArray.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>Aucune demande client pour le moment.</p>
              </div>
            ) : (
              <Tabs defaultValue="pending">
                <TabsList className="mb-4 grid h-auto w-full grid-cols-2 gap-1 sm:mb-6 sm:grid-cols-4">
                  <TabsTrigger value="pending" className="relative min-h-10">
                    Pending
                    {pendingEstimates.length > 0 && (
                      <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] text-white">
                        {pendingEstimates.length}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="approved" className="min-h-10">Approved</TabsTrigger>
                  <TabsTrigger value="completed" className="min-h-10">Completed</TabsTrigger>
                  <TabsTrigger value="rejected" className="min-h-10">Rejected</TabsTrigger>
                </TabsList>
                
                <TabsContent value="pending" className="mt-0">
                  {renderEstimateTable(pendingEstimates)}
                </TabsContent>
                
                <TabsContent value="approved" className="mt-0">
                  {renderEstimateTable(approvedEstimates)}
                </TabsContent>
                
                <TabsContent value="completed" className="mt-0">
                  {renderEstimateTable(completedEstimates)}
                </TabsContent>
                
                <TabsContent value="rejected" className="mt-0">
                  {renderEstimateTable(rejectedEstimates)}
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      )}

      <DevisGenerator
        open={isDevisOpen}
        onOpenChange={setIsDevisOpen}
        onDevisSaved={() => {
          refreshMadeDevis();
          setViewMode('made');
        }}
      />
      
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-h-[90dvh] max-w-[calc(100vw-1.5rem)] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Détails de la demande</DialogTitle>
            <DialogDescription>
              Consultez les informations de la demande client.
            </DialogDescription>
          </DialogHeader>
          
          {viewingEstimate && (
            <div className="mt-4 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Estimate ID</h3>
                    <p className="font-mono">{viewingEstimate.id}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
                    <div>{getStatusBadge(viewingEstimate.status)}</div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Created</h3>
                    <p>{formatDate(viewingEstimate.created_at)}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Customer Email</h3>
                    <p>{viewingEstimate.contact_email}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Customer Phone</h3>
                    <p>{viewingEstimate.contact_phone}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Shipping Address</h3>
                    <p className="whitespace-pre-line">
                      {formatShippingAddress(viewingEstimate.shipping_address)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium mb-3">Requested Items</h3>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {viewingEstimate.items && viewingEstimate.items.length > 0 ? (
                        viewingEstimate.items.map((item, index) => (
                          <TableRow key={`${item.product_id}-${index}`}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2} className="text-center py-4 text-muted-foreground">
                            No items in this estimate
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
              
              <div className="flex justify-between pt-4 border-t">
                <div className="space-x-2">
                  {viewingEstimate.status === 'pending' && (
                    <>
                      <Button 
                        variant="outline" 
                        className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                        onClick={() => {
                          handleUpdateStatus(viewingEstimate.id, 'rejected');
                        }}
                      >
                        <X size={16} className="mr-1" /> Reject
                      </Button>
                      <Button 
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => {
                          handleUpdateStatus(viewingEstimate.id, 'approved');
                        }}
                      >
                        <Check size={16} className="mr-1" /> Approve
                      </Button>
                    </>
                  )}
                  {viewingEstimate.status === 'approved' && (
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        handleUpdateStatus(viewingEstimate.id, 'completed');
                      }}
                    >
                      <CheckCircle size={16} className="mr-1" /> Mark as Completed
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="ml-2 border-purple-300 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
                    onClick={() => {
                      setIsViewDialogOpen(false);
                      handlePreviewEstimate(viewingEstimate);
                    }}
                  >
                    <Printer size={16} className="mr-1" /> Preview
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                    onClick={() => handleSendPreview(viewingEstimate)}
                  >
                    <Send size={16} className="mr-1" /> Send Preview
                  </Button>
                </div>
                
                <Button 
                  variant="outline"
                  onClick={() => setIsViewDialogOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-h-[92dvh] max-w-[calc(100vw-1rem)] overflow-y-auto p-4 sm:max-w-4xl sm:p-6">
          <DialogHeader>
            <DialogTitle>{previewTitle}</DialogTitle>
            <DialogDescription>
              Aperçu du document
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-2 sm:mt-4">
            <iframe 
              srcDoc={previewHtml}
              className="w-full rounded-md border bg-white" 
              style={{ height: 'min(70vh, 720px)' }}
            />
          </div>
          
          <DialogFooter>
            {viewingEstimate && (
              <Button 
                variant="outline" 
                className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                onClick={() => viewingEstimate && handleSendPreview(viewingEstimate)}
              >
                <Send size={16} className="mr-1" /> Send to Customer
              </Button>
            )}
            <Button 
              variant="outline"
              onClick={() => setIsPreviewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstimatesManagement;
